import { Message } from '@repo/utils/models/Message';
import { actionTypes as searchActionTypes } from '../../actions/search';
import type { AlertsState, ReduxAction } from '@repo/utils/types';
import SystemAlert from '@repo/utils/models/SystemAlert';

const initialAlertsState = {
  message: null,
  systemAlert: null,
  lastDismissedSystemAlert: null,
};

const resourceTypeToUserFacingName = {
  FEEDS: 'Home',
  SOURCE: 'Contact Information',
  INBOX: 'Review',
  INBOX_ITEM: 'Review Item',
  SEARCH_RESULTS: 'Search Results',
  _ALL_PEOPLE_SEARCH_RESULTS: 'People',
  LIST: 'Group',
  CONTACT_BATCH: 'Contacts',
  PAST_REMINDERS: 'Reminders',
  UPCOMING_REMINDERS: 'Reminders',
  CURRENTLY_EDITING_NOTE: 'Note',
  CONTACT_NOTES: 'Notes',
};

const methodToVerb = (method: string): string => {
  switch (method) {
    case 'FETCH_ALL':
    case 'FETCH':
      return 'Update';
    case 'CREATE':
    case 'UPDATE':
      return 'Save';
    case 'DELETE':
      return 'Delete';
    case 'MERGE':
      return 'Merge';
    default:
      return '';
  }
};

const resourceLabel = (resourceType: string) =>
  resourceTypeToUserFacingName[resourceType] || resourceType.toLowerCase();

export function alertsReducer(state: AlertsState = initialAlertsState, action: ReduxAction) {
  const { method, resourceType } = action.payload || {};
  let text = '';

  switch (action.type) {
    // We can dispatch directly, but can also use matching on the action type to do some automatic alerting
    case '*_REQUESTED':
      return { ...state, message: new Message({ type: 'loading' }) };

    case '*_SUCCEEDED':
      if (['FETCH', 'FETCH_ALL'].includes(method)) {
        return { ...state, message: null };
      } else {
        return { ...state, message: new Message({ type: 'success' }) };
      }

    case '*_FAILED':
      text = `Failed to ${methodToVerb(method)} ${resourceLabel(resourceType)}`;
      return { ...state, message: new Message({ type: 'error', text }) };

    case 'ALERT_USER':
      return {
        ...state,
        message: new Message({ type: action.payload.type, text: action.payload.text }),
      };

    case 'CLEAR_ALERTS':
    case searchActionTypes.CLEAR_SEARCH_TERM:
      return { ...state, message: null };

    case 'LOAD_LOCAL_STORE_SUCCEEDED':
      const lastDismissedSystemAlert = action.payload.get('last_dismissed_system_alert');

      if (state.systemAlert && state.systemAlert.publishedAt == lastDismissedSystemAlert) {
        return {
          ...state,
          systemAlert: state.systemAlert.copyWith({ isDismissed: true }),
          lastDismissedSystemAlert,
        };
      }
      return { ...state, lastDismissedSystemAlert };

    case 'FETCH_SYSTEM_ALERT_SUCCEEDED':
      let systemAlert = null;

      if (action.payload.response?.status) {
        systemAlert = new SystemAlert({
          ...action.payload.response.status,
          isDismissed: action.payload.response.status.publishedAt == state.lastDismissedSystemAlert,
        });
      }

      return { ...state, systemAlert };

    case 'DISMISS_SYSTEM_ALERT':
      return {
        ...state,
        systemAlert: state.systemAlert?.copyWith({ isDismissed: true }),
        lastDismissedSystemAlert: state.systemAlert?.publishedAt,
      };

    default:
      return state;
  }
}
